/**
 * The top of the project page: who the work is for, what state it is in, and
 * everything you can do to the record as a whole.
 *
 * Lifted out of `ProjectDetail` unchanged apart from the archive control. That
 * file is the frame - two fetches, eleven tabs and the rules about which reload
 * belongs to which of them - and the head had already grown a status field, a
 * document menu, an edit and a delete before the archive arrived. Another
 * button and another dialog in there would have pushed the frame past the size
 * where anybody can find the piece they came for (AGENTS.md §4), so the head
 * moved out first and the button went in afterwards.
 *
 * It holds no data of its own. The dialogs it can open are the exception, for
 * the same reason the row menu keeps its own: their state has to outlive the
 * click that opened them, and none of them needs anything from the page around
 * it. The delete dialog stays with `ProjectDetail`, because what follows a
 * successful delete is navigation away from a page this component knows nothing
 * about.
 *
 * ## Why Duplicate is here as well as in the row menu
 *
 * It was in the row menu alone, and that put it in the one place the operator
 * is NOT when they decide to reuse a job: deciding takes reading the brief, the
 * phases and the budget, all of which are on this page, and acting on it meant
 * going back to the register and finding the row again. The two entry points
 * open the same dialog and do the same thing afterwards - go to the copy - so
 * there is one behaviour with two doors rather than two behaviours.
 *
 * ## Why three of the actions are behind a kebab
 *
 * Because eight controls do not fit on a line beside a project name, and what
 * fell off the end of that line was Delete: alone on a second row, in red, under
 * a head with nothing else on it. `.crm-head-act` wraps by design, so the strip
 * was not broken - it was simply carrying more than a head can carry.
 *
 * The three that moved are the rare ones. A project is duplicated or archived
 * once in its life if at all, and Delete is the one control on this page that
 * should cost a deliberate second click rather than sit under the cursor. What
 * stayed out is what an operator opens the page to do: read the status, change
 * it, start a document, fix a detail.
 */
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArchiveRestore, ArchiveX, Copy, ExternalLink, Loader2, MoreVertical, Pencil, Pin, PinOff, Trash2 } from "lucide-react";
import { useT } from "../../../i18n/I18nProvider";
import type { Project, ProjectStatus, ProjectSummary } from "../../../lib/projects";
import { Btn, MenuField, PageHead, StatusPill, fmtStamp } from "../../ui";
import { Pop, useClosePops } from "../../shell/Pop";
import { UserAvatars } from "../_shared/UserChips";
import { NewForProjectMenu } from "./NewForProjectMenu";
import { ArchiveProjectDialog, ArchivedBadge, useUnarchiveProject } from "./ProjectArchive";
import { DuplicateProjectModal } from "./DuplicateProjectModal";
import { PinnedBadge, usePinProject } from "./ProjectPin";
import { useClientPreview } from "./useClientPreview";
import { ProjectSwitcher } from "./ProjectSwitcher";
import { STATUSES, statusTone } from "./status";

/** The separator between the parts of the head line. Decoration, so it is kept
 *  out of the accessible name the way the marks in `UserChips` are. */
function Dot() {
  return <span aria-hidden className="opacity-40">&middot;</span>;
}

/**
 * The line under the project name: who the work is for, who is on it, and when
 * it last moved.
 *
 * These two facts live HERE rather than on the KPI strip below, and the choice
 * is the point: a plate in a fixed-column grid cannot be left out, so an absent
 * value there has to be drawn as a dash, and a dash in a row of figures reads as
 * a number that failed to load. A sentence can simply be shorter. Both are also
 * the wrong kind of thing for that strip - one is a row of faces, the other a
 * date, and neither is a figure you compare against a budget.
 *
 * Zero members draws NOTHING. Not `UserAvatars` with an empty list, which would
 * correctly render its "nobody" mark for a table cell but reads as a broken
 * image in a head. Every project that came out of the old system has an empty
 * members array - the importer refused to invent owners when the host site holds
 * exactly one user - so this is the ordinary case today, not the edge, and a
 * stack of blanks on every project would look like an avatar service that is
 * down rather than like work nobody has been named for yet.
 */
function HeadMeta({ project, summary }: { project: Project; summary: ProjectSummary | null }) {
  const t = useT();
  return (
    <span className="flex flex-wrap items-center gap-x-2.5 gap-y-1">
      <span>{project.client?.name ?? "-"}</span>
      {project.members.length ? (
        <>
          <Dot />
          {/* The stack names its own people through `aria-label`; the word says
              what the row of faces IS, which the names alone do not. */}
          <span className="sr-only">{t("projects.team")}</span>
          <UserAvatars ids={project.members} max={4} size={22} />
        </>
      ) : null}
      {summary?.lastActivityAt ? (
        <>
          <Dot />
          <span>
            {t("projects.lastActivity")}: {fmtStamp(summary.lastActivityAt)}
          </span>
        </>
      ) : null}
    </span>
  );
}

/**
 * The three entries behind the kebab on the head: copy the project, take it off
 * the list, remove it.
 *
 * Its own component rather than a block of JSX inside the head, because a `Pop`
 * unmounts its children the moment it closes and this list has to close the menu
 * BEFORE it opens a dialog - so every entry here is a pair of calls, and a pair
 * of calls repeated six times inline is where one of them gets forgotten. The
 * dialogs themselves stay with `ProjectHead`, outside the popover, for the same
 * unmounting reason.
 *
 * The order is the row menu's (`ProjectRowMenu`): content first, then the ways
 * a project leaves the operator's day, in increasing order of finality. The
 * separator is what says the last one is not like the other two.
 */
function HeadMenuItems({
  archived,
  pinned,
  busy,
  pinBusy,
  clientViewBusy,
  onPin,
  onDuplicate,
  onClientView,
  onArchive,
  onUnarchive,
  onDelete,
}: {
  archived: boolean;
  /** Per-operator, so this is what THIS reader has bookmarked and not a fact
   *  about the project. Absent on a record fetched by an endpoint that does not
   *  attach it, which reads as not pinned - the entry still has to be offered,
   *  or such a project becomes unpinnable. */
  pinned: boolean;
  /** The unarchive request is in flight. Only that direction can be busy among
   *  the archive entries - the other one opens a dialog and returns. */
  busy: boolean;
  /** The pin write is in flight. Its own flag and not the one above: the two are
   *  different requests, and sharing a flag would grey out the archive entry
   *  because somebody clicked Pin. */
  pinBusy: boolean;
  onPin: () => void;
  onDuplicate: () => void;
  /** Mints a portal link and opens it. In flight while `clientViewBusy`. */
  onClientView: () => void;
  clientViewBusy: boolean;
  onArchive: () => void;
  onUnarchive: () => void;
  onDelete: () => void;
}) {
  const t = useT();
  const close = useClosePops();
  const pick = (run: () => void) => () => {
    close();
    run();
  };

  return (
    <>
      {/* First, and before the copy. It is the only entry here that changes
          nothing about the project - it changes what THIS operator's register
          looks like - so it belongs at the top where the reversible, private
          things go, not down among the ways a project leaves the day.

          One entry, two directions, the same as the archive below: a menu
          offering both would ask the operator to choose between the state the
          project is already in and the one it is not. */}
      <button type="button" className="crm-mi" disabled={pinBusy} onClick={pick(onPin)}>
        {pinBusy ? (
          <Loader2 className="size-4 animate-spin" />
        ) : pinned ? (
          <PinOff className="size-4" />
        ) : (
          <Pin className="size-4" />
        )}
        {t(pinned ? "projects.unpin" : "projects.pin")}
      </button>
      <button type="button" className="crm-mi" onClick={pick(onDuplicate)}>
        <Copy className="size-4" />
        {t("projects.duplicate")}
      </button>
      {/* Reading, and reading something that already exists - so it sits with
          the content entries rather than with the exits. It is deliberately NOT
          in the row menu: the question it answers ("is this page fit to send")
          is asked with the project open, and a register row is not where anybody
          decides that.

          It leaves the CRM: the entry mints a short-lived link and opens the
          REAL client portal in a new tab, so what the operator reads is the
          customer's own page rather than a reconstruction of it. The menu closes
          first, as every entry here does, because the popover unmounts its
          children and the request has to outlive the click. */}
      <button type="button" className="crm-mi" disabled={clientViewBusy} onClick={pick(onClientView)}>
        {clientViewBusy ? <Loader2 className="size-4 animate-spin" /> : <ExternalLink className="size-4" />}
        {t("projects.clientView.action")}
      </button>
      {/* One entry, two directions - a menu offering both would ask the operator
          to choose between the state the project is already in and the one it is
          not, which is not a choice. */}
      {archived ? (
        <button type="button" className="crm-mi" disabled={busy} onClick={pick(onUnarchive)}>
          {busy ? <Loader2 className="size-4 animate-spin" /> : <ArchiveRestore className="size-4" />}
          {t("projects.unarchive")}
        </button>
      ) : (
        <button type="button" className="crm-mi" onClick={pick(onArchive)}>
          <ArchiveX className="size-4" />
          {t("projects.archive")}
        </button>
      )}
      <div className="crm-menu-sep" />
      <button type="button" className="crm-mi crm-mi-danger" onClick={pick(onDelete)}>
        <Trash2 className="size-4" />
        {t("common.delete")}
      </button>
    </>
  );
}

export function ProjectHead({
  project,
  summary,
  onStatus,
  onExpenseSaved,
  onEdit,
  onDelete,
  onArchiveChanged,
  onPinChanged,
}: {
  project: Project;
  summary: ProjectSummary | null;
  onStatus: (s: ProjectStatus) => void;
  onExpenseSaved: () => void;
  onEdit: () => void;
  onDelete: () => void;
  /** The saved row, so the page can swap the copy it is holding rather than
   *  refetch. Both directions come through here. */
  onArchiveChanged: (next: Project) => void;
  /**
   * The pin moved. Only the flag travels, not a saved row: the pin endpoints
   * answer with the pin and NOT with the project, so there is nothing to spread
   * over the record the page is holding - and spreading a partial project over
   * one that carries its milestone array would empty the milestones tab, which
   * is the trap `applyEdit` and `onArchiveChanged` both spell out.
   */
  onPinChanged: (pinned: boolean) => void;
}) {
  const t = useT();
  const navigate = useNavigate();
  const [archiving, setArchiving] = useState(false);
  const [duplicating, setDuplicating] = useState(false);
  const unarchive = useUnarchiveProject();
  const pin = usePinProject();
  const clientPreview = useClientPreview();
  const archived = !!project.archivedAt;

  return (
    <>
      <PageHead
        title={
          // The switcher rides with the NAME rather than with the action strip,
          // and that is the whole point of it: it is not something you do to
          // this project, it is the way to a different one. Beside the title it
          // reads as "this name, and there are others"; among the buttons it
          // would read as one more thing that changes the record on screen.
          //
          // It is also the one control that must not come out of the title's
          // width the way `.crm-head-act` does - hence a flex row here, with the
          // name shrinkable and the 34px circle fixed beside it.
          <span className="flex min-w-0 items-center gap-2.5">
            <span className="min-w-0">{project.name}</span>
            <ProjectSwitcher project={project} />
          </span>
        }
        sub={<HeadMeta project={project} summary={summary} />}
        actions={
          <>
            <StatusPill tone={statusTone(project.status)}>
              {t(`projects.status.${project.status}`)}
            </StatusPill>
            {/* Beside the status and not instead of it. The project is still
                `in_progress` or `completed` underneath - the archive says it is
                no longer being offered, which is a different fact. The register
                hides these rows, so this page is where an archived project is
                usually met, and meeting it with no mark at all is how a record
                whose state matters becomes a record whose state is invisible. */}
            <ArchivedBadge project={project} />
            {/* Beside the archive mark, and muted for the same reason: it is not
                a lifecycle state. It is here at all because the pin is otherwise
                invisible from the page you pin from - the strip that shows it
                lives on the register, one screen away. */}
            <PinnedBadge project={project} />
            <MenuField
              value={project.status}
              onChange={onStatus}
              label={t("projects.statusCol")}
              options={STATUSES.map((s) => ({ value: s, label: t(`projects.status.${s}`) }))}
            />
            {/* Starting a document that already knows its project and its
                client. It sits before Edit because it is the action an operator
                comes to this page to perform, where Edit and Delete are things
                they came to fix. Which entries it offers, and why contracts and
                tickets are not among them, is argued in the menu's own file. */}
            <NewForProjectMenu project={project} onExpenseSaved={onExpenseSaved} />
            <Btn onClick={onEdit}>
              <Pencil className="size-4" />
              {t("projects.edit")}
            </Btn>
            {/* Everything else behind one kebab. The head used to draw Duplicate,
                Archive and Delete as three more worded pills, and eight controls
                do not fit on one line beside a project name: the strip wrapped,
                and what it dropped onto the second row was Delete, alone, in red,
                under a head that had nothing else on that line.

                Which three went in is the point rather than which three fit. Two
                of them are rare - a project is copied or archived once in its
                life, if ever - and the third must not be easy to hit. Status,
                New and Edit stay out here because they are what an operator came
                to the page to do. The order inside is the row menu's own, so the
                same three entries are in the same sequence in both places. */}
            <Pop
              trigger={(open) => (
                <Btn
                  round
                  className={open ? "is-on" : undefined}
                  title={t("common.more")}
                  aria-label={t("common.more")}
                  aria-haspopup="menu"
                  aria-expanded={open}
                >
                  <MoreVertical className="size-4" />
                </Btn>
              )}
            >
              <HeadMenuItems
                archived={archived}
                pinned={!!project.pinned}
                busy={unarchive.busy}
                pinBusy={pin.busy}
                clientViewBusy={clientPreview.busy}
                onPin={() => void pin.run(project, onPinChanged)}
                onDuplicate={() => setDuplicating(true)}
                onClientView={() => void clientPreview.open(project.id)}
                onArchive={() => setArchiving(true)}
                onUnarchive={() => void unarchive.run(project, onArchiveChanged)}
                onDelete={onDelete}
              />
            </Pop>
          </>
        }
      />

      {/* Straight to the copy, the same as the row menu. Staying on the source
          with a toast is the state in which the button gets pressed a second
          time - and a copy has no idempotency key, so that is a second project.
          Nothing here is told the page changed: the route change unmounts it. */}
      {duplicating ? (
        <DuplicateProjectModal
          project={project}
          onClose={() => setDuplicating(false)}
          onCreated={(created) => {
            setDuplicating(false);
            navigate(`/projects/${created.id}`);
          }}
        />
      ) : null}

      {archiving ? (
        <ArchiveProjectDialog
          project={project}
          onClose={() => setArchiving(false)}
          onArchived={(next) => {
            setArchiving(false);
            onArchiveChanged(next);
          }}
        />
      ) : null}
    </>
  );
}
